// NavigationBar.jsx

// Copyright HS Analysis GmbH, 2020
// Author: Sebastian Murgul, Viktor Eberhardt

// Framework imports
import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";

// External npm packages
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import {
  ArrowDropDown,
  Person,
  ExitToApp,
  CoPresent,
  Build,
  Info,
  History,
} from "@mui/icons-material";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import WorkIcon from "@mui/icons-material/Work";
import BarChartIcon from "@mui/icons-material/BarChart";
import ScannerIcon from "@mui/icons-material/Scanner";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import withStyles from "@mui/styles/withStyles";

// HSA Packages
import Backend from "../utils/Backend";
import { authenticationService } from "../services";
import { viewerType } from "../utils/Utils";

const styles = (theme) => ({
  root: {
    height: "100vh",
  },
  appBar: {
    height: 64,
    // Ensure solid background and no visual clipping
    backgroundColor: undefined, // keep theme default
    overflow: "visible",
    [theme.breakpoints.down("sm")]: {
      boxShadow: "none", // avoid white-looking shadow line under the header on phones
    },
  },
  // Make the top navigation horizontally scrollable on small screens
  toolbar: {
    display: "flex",
    overflowX: "auto",
    overflowY: "visible",
    whiteSpace: "nowrap",
    WebkitOverflowScrolling: "touch",
    alignItems: "center",
    minHeight: 64,
    height: 64,
    width: "100%",
    boxSizing: "border-box",
    paddingTop: 0,
    paddingBottom: 0,
    overscrollBehaviorX: "contain",
    // Hide scrollbar visuals across browsers while keeping scroll functionality
    msOverflowStyle: "none", // IE and Edge
    scrollbarWidth: "none", // Firefox
    // Subtle scrollbar for webkit-based browsers
    "&::-webkit-scrollbar": {
      height: 0,
      display: "none",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent",
      borderRadius: 0,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
  },
  headerTitle: {
    flexGrow: 1,
    lineHeight: "22px",
    textOverflow: "ellipsis",
    WebkitLineClamp: 2,
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    display: "-webkit-box",
    [theme.breakpoints.down("sm")]: {
      display: "none", // Save space on phones
    },
  },
  grow: {
    flexGrow: 1,
    [theme.breakpoints.down("sm")]: {
      flexGrow: 0, // Do not push actions off-screen on phones
      display: "none",
    },
  },
  logoWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    marginLeft: -12,
    marginRight: 20,
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0, // avoid pushing content off-screen on phones
    },
  },
  menuButton: {
    marginLeft: 0,
    marginRight: 0,
    height: 50,
    cursor: "pointer",
  },
  headerLabel: {
    position: "absolute",
    top: 46,
    left: 58,
    color: "#fff",
    fontSize: 11,
    opacity: 0.85,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  appbarPlaceholder: theme.mixins.toolbar,
  headerButton: {
    textTransform: "none",
    height: 64,
    padding: "0 20px",
    borderRadius: 0,
    flex: "0 0 auto", // Prevent shrinking so horizontal scroll works
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      padding: "0 12px",
      minWidth: "unset",
    },
  },
  menu: {
    transformOrigin: "center top",
  },
  menuIcon: {
    marginRight: 10,
    color: "#444",
  },
  warningColor: {
    color: theme.palette.warning.main,
  },
  errorColor: {
    color: theme.palette.error.main,
  },
});

class NavigationBar extends Component {
  constructor(props) {
    super(props);

    this._isMounted = false;
    this.state = {
      currentUser: null,
      open: false,
      headline: "",
      headerLabel: "",
      showDevButtons: false,
      resultsPath: "",
      isResultsButtonVisible: false,
      extraButtonState: "", //none, results, project
      scannerState: null,
      expirationMessage: "",
      canAccessLicensingPage: false,
      canAccessAdminPage: false,
      canAccessAITab: false,
      canAccessCases: false,
      canAccessScan: false,
      canAccessFileView: true,
    };

    window.setNavigationbarTitle = this.setNavigationbarTitle;
  }

  // check if report button should be shown
  // and check if report exist
  checkProjectButton = (pathname) => {
    let isResultsButtonVisible =
      pathname.includes("/view/") || pathname.includes("/esr_view/");
    if (this.state.isResultsButtonVisible !== isResultsButtonVisible) {
      this.setMountedState({ isResultsButtonVisible });
    }
    if (isResultsButtonVisible) {
      let uuid = pathname.split("view/").slice(-1)[0];
      let dir_path = "/" + uuid;
      Backend.walkProjectDirFiles(dir_path, (result) => {
        let reportExists = false;
        for (let row of result) {
          if (
            row.path.endsWith("report.xlsx") ||
            row.path.endsWith("report.xlsm")
          ) {
            reportExists = true;
            break;
          }
        }
        this.setMountedState({
          resultsPath: reportExists ? "/report/" + uuid : "",
          extraButtonState: "results",
        });
      });
    } else {
      if (pathname.includes("/report/")) {
        const uuid = pathname.split("report/").slice(-1)[0];
        Backend.loadProject(uuid)
          .then((project) => {
            this.setMountedState({
              extraButtonState: "project",
              projectPath: viewerType(project.type) + uuid,
            });
          })
          .catch(() => {
            console.info(
              "Project not found, probably a merged results project!"
            );
          });
      } else {
        this.setMountedState({
          extraButtonState: "",
          projectPath: "",
        });
      }
    }
  };

  componentDidMount() {
    this._isMounted = true;
    if (this.props.location.pathname === "/iframe.html") return;

    //on first load
    this.checkProjectButton(location.pathname);
    this.setPageHeadline();

    //on path change
    this.props.history.listen((location) => {
      this.setPageHeadline();
      this.checkProjectButton(location.pathname);
    });

    authenticationService.currentUser.subscribe((x) => {
      if (!x) return;
      this.setMountedState({
        currentUser: x,
      });
      this.updateUserGroups();
    });

    Backend.getLicensingInfo((license) => {
      if (license.licenseStatus === "VALID") {
        this.setMountedState({
          editableProject: null,
          open: false,
        });
      } else {
        authenticationService.logout();
        this.props.history.push("/licensing");
      }
      let scannerState = "INVALID";
      let expirationMessage = "";
      if (license.activeModules.length === 0) {
        //scannerState = "VALID";
        scannerState = "INVALID"; // Invalid, because not working in this version, TODO: fix in future version
      } else {
        const scannerData = license.activeModules.find(
          (item) => item.name === "HSAScanner"
        );
        if (!scannerData) return;

        const expirationDays = scannerData.expiresInDays;
        if (expirationDays < 31) {
          if (expirationDays < 0) {
            expirationMessage =
              expirationDays === -1
                ? "Expired for 1 day!"
                : "Expired for " + expirationDays + " days!";
          } else {
            expirationMessage =
              expirationDays === -1
                ? "Expires in 1 day!"
                : "Expires in " + expirationDays + " days!";
          }
        }
        scannerState = scannerData.isExpired ? "EXPIRED" : "VALID";
      }

      this.setMountedState({ scannerState, expirationMessage });
    });

    // Load header label from settings
    Backend.readImporterSettings((data) => {
      if (!this._isMounted) return;
      const headerLabel =
        data && (data.HeaderLabel || data.headerLabel)
          ? data.HeaderLabel || data.headerLabel
          : "";
      this.setState({ headerLabel });
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setMountedState = (stateObject, callback) => {
    if (this._isMounted) {
      this.setState(stateObject, callback);
    }
  };

  updateUserGroups = () => {
    Backend.getCurrentUser((user) => {
      if (!user.group) return;

      this.setMountedState({
        canAccessLicensingPage: user.group.canAccessLicensingPage,
        canAccessAdminPage: user.group.canAccessAdminPage,
        canAccessAITab: user.group.canAccessAITab,
        canAccessCases: user.group.canAccessCases,
        canAccessScan: user.group.canAccessScanner,
      });
    });
  };

  setNavigationbarTitle = (title) => {
    this.setMountedState({ headline: title });
  };

  logout = () => {
    authenticationService.logout();
    this.props.history.push("/login");
  };

  handleMenu() {
    this.setMountedState((state) => ({ open: !state.open }));
  }

  handleClose(event) {
    if (this.anchorEl.contains(event.target)) {
      return;
    }

    this.setMountedState({ open: false });
  }

  setPageHeadline() {
    if (this.props.location.pathname === "/iframe.html") return;
    let path = window.location.href;
    const search = window.location.search || "";
    const query = new URLSearchParams(search);
    const titleMap = {
      "/view/": "Viewer",
      "/report/": "Results",
      "/admin": "Admin Settings",
      "/login": "Login",
      "/about": "About",
      "/cases": "Cases",
      "/file_view": "Viewer",
      "/ai_view": "AI",
      "/classroom": "Classroom",
      "/prediction": "Prediction",
      "/licensing": "Licenseing",
      "/scan_view": "Scan Viewer",
    };
    Backend.getVersion((data) => {
      let title = "Projects";
      for (const pathFragment in titleMap) {
        if (path.includes(pathFragment)) {
          title = titleMap[pathFragment];
          // If we are in file_view but a caseId is present, the semantic section is Cases
          if (
            pathFragment === "/file_view" &&
            (query.has("caseId") || /[?&]caseId=/.test(search))
          ) {
            title = "Cases";
          }
          break;
        }
      }

      const fullVersion = data.version.split(".");
      const versionLength = fullVersion.length;
      const version =
        versionLength > 3 ? fullVersion.slice(0, 3).join(".") : data.version;
      window.document.title = `${title} - HSA KIT ${version}`;
    });
    let headline = "Home";
    for (const pathFragment in titleMap) {
      if (path.includes(pathFragment)) {
        // if pathFragment "/file_view" and path looks like "/file_view/{uuid}"
        if (
          pathFragment === "/file_view" &&
          path.match(
            /\/file_view\/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/
          )
        ) {
          headline = "Cases";
          break;
        }
        // If navigating to /file_view with a caseId query, show Cases as selected in the nav
        if (
          pathFragment === "/file_view" &&
          (query.has("caseId") || /[?&]caseId=/.test(search))
        ) {
          headline = "Cases";
          break;
        }
        headline = titleMap[pathFragment];
        break;
      }
    }
    if (this.state.headline !== headline) {
      this.setMountedState({ headline: headline });
    }
    return headline;
  }

  /**
   * Handles the click event for navigating to the "/cases" route.
   * - If the current pathname is "/cases", it toggles the URL hash between "#refresh1" and "#refresh2"
   *   to trigger a minimal change in the URL.
   * - If the current pathname is not "/cases", it navigates to the "/cases" route.
   */
  handleCaseClick = () => {
    if (this.props.location.pathname === "/cases") {
      const currentHash = this.props.location.hash;
      const newHash = currentHash === "#refresh1" ? "#refresh2" : "#refresh1"; // Alternate between two hashes
      this.props.history.replace({
        pathname: "/cases",
        hash: newHash, // Minimal change to the URL
      });
    } else {
      this.props.history.push("/cases");
    }
  };

  render() {
    const {
      currentUser,
      open,
      showDevButtons,
      scannerState,
      expirationMessage,
      canAccessLicensingPage,
      canAccessAdminPage,
    } = this.state;
    const { classes } = this.props;

    return (
      <AppBar className={classes.appBar} position="fixed">
        <Toolbar className={classes.toolbar}>
          <div
            className={classes.logoWrapper}
            style={
              this.props.navControlsHidden ? { cursor: "default" } : undefined
            }
            onClick={
              this.props.navControlsHidden
                ? undefined
                : () => this.props.history.push("/")
            }
          >
            <img
              className={classes.menuButton}
              alt="hsa logo"
              src="/img/hsa_logo.png"
            />
            {this.state.headerLabel && (
              <span className={classes.headerLabel}>
                {this.state.headerLabel}
              </span>
            )}
          </div>
          <Typography
            variant="h6"
            color="inherit"
            className={classes.headerTitle}
            onClick={() => Backend.ping()}
          >
            {this.state.headline}
          </Typography>
          <Typography variant="h6" color="inherit" className={classes.grow} />
          {currentUser ? (
            this.props.navControlsHidden ? null : (
              <React.Fragment>
                {/* hidden log file download button */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    height: "100%",
                    width: 20,
                  }}
                  onClick={() => {
                    Backend.getAbout((result) => {
                      if (result.logFiles && result.logFiles.length > 0) {
                        let newestLogFile = result.logFiles[0];

                        const a = document.createElement("a");
                        let url =
                          "api/about/logfile?name=" +
                          window.decodeURIComponent(newestLogFile);
                        a.href = url;
                        a.download = url.split("/").pop();
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }
                    });
                  }}
                ></div>
                <Button
                  style={{
                    position: "relative",
                    minWidth: 130,
                    background:
                      this.state.headline === "Home"
                        ? "rgba(0,0,0,0.2)"
                        : "none",
                  }}
                  onClick={() => {
                    this.props.history.push("/");
                    this.setMountedState({ headline: "Home" });
                  }}
                  className={classes.headerButton}
                  color="inherit"
                >
                  <WorkIcon
                    style={{
                      position: "absolute",
                      top: 17,
                      left: 22,
                      filter: "drop-shadow( -2px 2px 0px rgba(6,115,193,1))",
                      zIndex: 1,
                    }}
                  />
                  <WorkIcon
                    style={{
                      marginRight: 5,
                      position: "relative",
                      right: 3,
                      top: 2,
                    }}
                  />
                  <strong>Projects</strong>
                </Button>

                <Button
                  disabled={!this.state.canAccessAITab}
                  style={{
                    position: "relative",
                    background:
                      this.state.headline === "AI Viewer"
                        ? "rgba(0,0,0,0.2)"
                        : "none",
                  }}
                  onClick={() => {
                    this.props.history.push("/ai_view");
                    this.setMountedState({ headline: "AI Viewer" });
                  }}
                  className={classes.headerButton}
                  color="inherit"
                >
                  <BarChartIcon
                    style={{
                      marginRight: 5,
                      position: "relative",
                      right: 3,
                      top: 2,
                    }}
                  />
                  <strong>AI</strong>
                </Button>

                <Button
                  disabled={!this.state.canAccessCases}
                  style={{
                    background:
                      this.state.headline === "Cases"
                        ? "rgba(0,0,0,0.2)"
                        : "none",
                  }}
                  onClick={this.handleCaseClick}
                  className={classes.headerButton}
                  color="inherit"
                >
                  <PermMediaIcon style={{ marginRight: 5 }} />
                  <strong>Cases</strong>
                </Button>

                <Tooltip
                  title={this.state.canAccessFileView ? "" : "No Permission!"}
                >
                  <span>
                    <Button
                      style={{
                        background:
                          this.state.headline === "Viewer"
                            ? "rgba(0,0,0,0.2)"
                            : "none",
                      }}
                      disabled={!this.state.canAccessFileView}
                      onClick={() => this.props.history.push("/file_view")}
                      className={classes.headerButton}
                      color="inherit"
                    >
                      <FolderCopyIcon style={{ marginRight: 5 }} />
                      <strong>Files</strong>
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title={expirationMessage}>
                  <span>
                    <Button
                      disabled={
                        scannerState !== "VALID" || !this.state.canAccessScan
                      }
                      style={{
                        position: "relative",
                        background:
                          this.state.headline === "Scan Viewer"
                            ? "rgba(0,0,0,0.2)"
                            : "none",
                      }}
                      onClick={() => {
                        this.props.history.push("/scan_view");
                      }}
                      className={classes.headerButton}
                      color="inherit"
                    >
                      <ScannerIcon
                        style={{
                          marginRight: 5,
                        }}
                        className={
                          scannerState !== "VALID" || expirationMessage === ""
                            ? ""
                            : classes.warningColor
                        }
                      />
                      <strong
                        className={
                          scannerState !== "VALID" || expirationMessage === ""
                            ? ""
                            : classes.warningColor
                        }
                      >
                        Scan
                      </strong>
                    </Button>
                  </span>
                </Tooltip>

                {this.state.extraButtonState !== "" && (
                  <React.Fragment>
                    {this.state.extraButtonState === "results" ? (
                      <Button
                        style={{
                          background:
                            this.state.headline &&
                            this.state.headline.includes("Results:")
                              ? "rgba(0,0,0,0.2)"
                              : "none",
                        }}
                        disabled={this.state.resultsPath === ""}
                        onClick={() =>
                          this.props.history.push(this.state.resultsPath)
                        }
                        className={classes.headerButton}
                        color="inherit"
                      >
                        <SvgIcon style={{ marginRight: 5 }}>
                          <svg
                            aria-hidden="true"
                            focusable="false"
                            data-prefix="fas"
                            data-icon="table"
                            className="svg-inline--fa fa-table fa-w-16"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                          >
                            <path
                              fill="currentColor"
                              d="M464 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zM224 416H64v-96h160v96zm0-160H64v-96h160v96zm224 160H288v-96h160v96zm0-160H288v-96h160v96z"
                            ></path>
                          </svg>
                        </SvgIcon>
                        <strong>Results</strong>
                      </Button>
                    ) : (
                      <Button
                        disabled={this.state.projectPath === ""}
                        onClick={() => {
                          this.props.history.push(this.state.projectPath);
                        }}
                        className={classes.headerButton}
                        color="inherit"
                      >
                        <WorkIcon style={{ marginRight: 5 }} />
                        <strong>Project</strong>
                      </Button>
                    )}
                  </React.Fragment>
                )}

                {showDevButtons && (
                  <React.Fragment>
                    <Button
                      style={{
                        background:
                          this.state.headline === "Prediction"
                            ? "rgba(0,0,0,0.2)"
                            : "none",
                      }}
                      onClick={() => this.props.history.push("/prediction")}
                      className={classes.headerButton}
                      color="inherit"
                    >
                      <WorkIcon style={{ marginRight: 5 }} />
                      <strong>Prediction</strong>
                    </Button>
                    <Button
                      style={{
                        background:
                          this.state.headline === "Classroom"
                            ? "rgba(0,0,0,0.2)"
                            : "none",
                      }}
                      onClick={() => {
                        if (window.location.href.includes("/view/")) {
                          window.toggleClassRoomChat();
                        } else {
                          window.openWarningDialog(
                            "Please open a Project first"
                          );
                        }
                      }}
                      className={classes.headerButton}
                      color="inherit"
                    >
                      <ScreenShareIcon style={{ marginRight: 5 }} />
                      <strong>Classroom</strong>
                    </Button>
                  </React.Fragment>
                )}

                <Button
                  ref={(node) => {
                    this.anchorEl = node;
                  }}
                  className={classes.headerButton}
                  aria-owns={open ? "menu-appbar" : undefined}
                  aria-haspopup="true"
                  onClick={() => this.handleMenu()}
                  color="inherit"
                >
                  <Person style={{ marginRight: 5 }} />
                  <strong>{currentUser.fullName}</strong>
                  <ArrowDropDown style={{ marginLeft: 5 }} />
                </Button>
                <Popper
                  open={open}
                  anchorEl={this.anchorEl}
                  transition
                  placement="bottom-end"
                  style={{ zIndex: 1500 }}
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8], // 8px vertical spacing below the button
                      },
                    },
                    {
                      name: "preventOverflow",
                      options: {
                        altAxis: true,
                        tether: true,
                      },
                    },
                  ]}
                >
                  {({ TransitionProps }) => (
                    <Grow
                      {...TransitionProps}
                      id="menu-list-grow"
                      className={classes.menu}
                    >
                      <Paper square style={{ minWidth: 220 }}>
                        <ClickAwayListener
                          onClickAway={(e) => this.handleClose(e)}
                        >
                          <MenuList style={{ padding: 0 }}>
                            {canAccessAdminPage && (
                              <MenuItem
                                onClick={() =>
                                  this.props.history.push("/admin")
                                }
                              >
                                <CoPresent className={classes.menuIcon} />
                                Admin
                              </MenuItem>
                            )}
                            {canAccessLicensingPage && (
                              <MenuItem
                                onClick={() => {
                                  this.props.history.push("/licensing");
                                }}
                              >
                                <Build className={classes.menuIcon} />
                                Licensing Page
                              </MenuItem>
                            )}
                            <MenuItem
                              onClick={() =>
                                this.props.history.push("/userhistory")
                              }
                            >
                              <History className={classes.menuIcon} />
                              User Access History
                            </MenuItem>
                            <MenuItem
                              onClick={() => this.props.history.push("/about")}
                            >
                              <Info className={classes.menuIcon} />
                              About / Help
                            </MenuItem>
                            <MenuItem onClick={this.logout}>
                              <ExitToApp className={classes.menuIcon} />
                              Logout
                            </MenuItem>
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </React.Fragment>
            )
          ) : (
            <Button
              color="inherit"
              onClick={() => this.props.history.push("/login")}
            >
              Sign In
            </Button>
          )}
        </Toolbar>
      </AppBar>
    );
  }
}

NavigationBar.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object,
  location: PropTypes.object,
  navControlsHidden: PropTypes.bool,
};

export default withRouter(withStyles(styles)(NavigationBar));
